import { useMemo, useState } from 'react';
import { Modal } from 'antd';
import { useSelector } from 'react-redux';
import { Marker } from 'react-google-maps';
import HarvestCompleteModal from '../../farm-modals/HarvestCompleteModal';
import { sendSingleRequest } from '../../../apis';
import { ILocation, TBusinessType } from '../../../entities/general.entities';
import { translate } from '../../../lib/lang.helper';
import { selectLang } from '../../../store/ui/ui.selector';
import SeedingsOrder from './SeedingsOrder';
import AssessmentModal from '../../farm-modals/AssessmentModal';
import {
  ILineDetail,
  IMusselLineResource,
  IOysterLineResource,
} from '../../../entities/farms.entities';
import { IMapSeedPoint } from '../../../entities/ui.entities';
import { selectFarmsData } from '../../../store/farms/farms.selector';
import { defaultDateFormat } from '../../../lib/common.helpers';

declare namespace google {
  export namespace maps {
    class Map {
      constructor(mapDiv: HTMLElement, opts?: any);
    }
    class LatLngBounds {
      constructor(southWest?: LatLng, northEast?: LatLng);
      extend(point: LatLng): void;
      getCenter(): LatLng;
      contains(latLng: LatLng): boolean;
    }

    class LatLng {
      constructor(lat: number, lng: number);
    }
    class AdvancedMarkerElement {
      constructor(opts?: any);
    }
  }
}

const isInViewport = (
  position: ILocation,
  bounds: google.maps.LatLngBounds | null,
) => {
  if (!bounds) return false;
  return bounds.contains(new google.maps.LatLng(position.lat, position.lng));
};

interface Props {
  lineID: number;
  bounds: google.maps.LatLngBounds | null;
  point: IMapSeedPoint;
  seedPositions: IMapSeedPoint[];
}

const DottedLine = ({ lineID, bounds, point, seedPositions }: Props) => {
  const lang = useSelector(selectLang);
  const farmsData = useSelector(selectFarmsData);

  const { growingSeeds, mainSeedID, lineCapacity, cropType } = useMemo(() => {
    for (const farm of farmsData) {
      for (const line of farm.lines) {
        if (line.id === lineID) {
          let lineCapacity = 0;
          if (line.crop_type === 'MUSSEL') {
            const lnD = line as IMusselLineResource;
            lineCapacity = Math.max(
              lnD.backbone,
              lnD.growing_cycle?.current_line_length ?? 0,
            );
          } else if (line.crop_type === 'OYSTER') {
            const lnD = line as IOysterLineResource;
            lineCapacity = Math.max(
              lnD.basket_count,
              lnD.growing_cycle?.current_basket_count ?? 0,
            );
          }
          return {
            growingSeeds:
              line.growing_cycle?.seedings.map(s => {
                const pos = seedPositions.find(y => y.seed_data?.id === s.id);
                return {
                  ...s,
                  start_position:
                    pos?.seed_data?.start_position ?? s.start_position,
                  end_position: pos?.seed_data?.end_position ?? s.end_position,
                };
              }) ?? [],
            mainSeedID: line.growing_cycle?.main_seed.id,
            lineCapacity,
            cropType: line.crop_type,
          };
        }
      }
    }
    return {
      growingSeeds: [],
      mainSeedID: 0,
      lineCapacity: 0,
      cropType: 'MUSSEL' as TBusinessType,
    };
  }, [farmsData, lineID]);

  const [visibleInfo, setVisibleInfo] = useState(false);
  const [visibleHarvest, setVisibleHarvest] = useState(false);
  const [visibleAssessment, setVisibleAssessment] = useState(false);
  const [lineData, setLineData] = useState<ILineDetail>();
  const [loadingLineData, setLoadingLineData] = useState(false);
  const [showMove, setShowMove] = useState(false);

  const marker = isInViewport(point.start, bounds) ? point : null;

  const handleHarvestLoad = async (e: any, type: string) => {
    e.preventDefault();
    setLoadingLineData(true);
    const response = await sendSingleRequest(
      { with_harvest_spats: true },
      'GET',
      `api/farm/line/lines/${lineID}`,
      true,
    );
    if (response.status) {
      if (type === 'harvest') {
        setVisibleAssessment(true);
      }
      if (type === 'assess') {
        setVisibleAssessment(true);
      }
      setLineData(response.data);
      setLoadingLineData(false);
    } else {
      alert('Failed to load line data');
      setLoadingLineData(false);
    }
  };

  const prepareData = () => {
    const tmp: any = { ...lineData };
    tmp.harvest_groups = [{ batch_seed_id: point.seed_data?.id }];
    tmp.is_final = false;
    return tmp;
  };

  const prepareAssessmentData = () => {
    const tmp: any = { ...lineData };
    tmp.seeding_id = mainSeedID;
    tmp.origin_spat_id = point.seed_data?.spat_source_id;
    return tmp;
  };

  return (
    <>
      {!!marker && (
        <Marker
          position={point.start}
          icon={{
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="red" width="24px" height="24px">
            <circle cx="12" cy="12" r="10" fill="white" />
            <text x="12" y="16" text-anchor="middle" fill="black" font-size="12px" font-family="Arial" font-weight="bold">!</text>
          </svg>
        `)}`,
          }}
          onClick={() => setVisibleInfo(true)}
        />
      )}
      {visibleInfo && point.seed_data && (
        <Modal
          visible={true}
          onCancel={e => setVisibleInfo(false)}
          footer={null}
          width={1200}
        >
          <div>
            <p>
              <strong>{translate(lang, 'Seeding date')}: </strong>
              {defaultDateFormat(point.seed_data.planned_date_seed)}
            </p>
            <p>
              <strong>
                {translate(lang, 'Spat ID')} {point.seed_data.spat_source}
              </strong>
            </p>
            <p>
              <strong>
                {translate(lang, 'Storage type')}: {cropType}
              </strong>
            </p>
            <p>
              <strong>
                {cropType === 'OYSTER'
                  ? translate(lang, 'Number of baskets')
                  : translate(lang, 'Length')}
                : {point.seed_data.line_length ?? point.seed_data.basket_count}
              </strong>
            </p>
            <button
              className='rsp-btn button button--2 button--green button--small button--fill'
              disabled={loadingLineData}
              onClick={e => handleHarvestLoad(e, 'harvest')}
            >
              {loadingLineData
                ? translate(lang, 'Loading...')
                : translate(lang, 'Harvest')}
            </button>
            <button
              className='rsp-btn button ml-10 button--2 button--green button--small button--fill'
              disabled={loadingLineData}
              onClick={e => handleHarvestLoad(e, 'assess')}
            >
              {loadingLineData
                ? translate(lang, 'Loading...')
                : translate(lang, 'Assess')}
            </button>
            {!showMove && (
              <button
                className='rsp-btn button ml-10 button--2 button--blue button--small button--fill'
                onClick={e => setShowMove(true)}
              >
                {translate(lang, 'Move')}
              </button>
            )}
          </div>
          {showMove && (
            <SeedingsOrder
              lineID={lineID}
              points={growingSeeds}
              movableSeed={point.seed_data.id}
              capacity={lineCapacity}
              onClose={() => setShowMove(false)}
            />
          )}
        </Modal>
      )}
      {visibleHarvest && lineData && (
        <HarvestCompleteModal
          visible={visibleHarvest}
          title={''}
          lineData={lineData}
          data={prepareData()}
          onCancel={() => setVisibleHarvest(false)}
          onConfirm={() => {}}
          onlyView={false}
          warningMessage={''}
          lineChangeable={false}
        />
      )}
      {visibleAssessment && lineData && (
        <AssessmentModal
          visible={visibleAssessment}
          title={''}
          lineData={lineData}
          data={prepareAssessmentData()}
          onCancel={() => setVisibleAssessment(false)}
          onConfirm={() => setVisibleAssessment(false)}
          onlyView={false}
          lineChangeable={false}
        />
      )}
    </>
  );
};

export default DottedLine;
